import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TableBody, TableRow, TableCell, Box, Avatar, Tooltip, Icon } from '@mui/material';
import { More } from '../More';
import { formatDate } from '../../../../utils/formatDate';
import { getMailingColorStatus } from '../../../mailing/constants';
import { useHistory } from 'react-router-dom';
import { getRouteWithParam, ROUTE_FOR_ASSETS, ROUTES } from '../../../../constants/routes';
import { DISABLED_CONTROL_TOOLTIP } from '../../../../constants/variables';
import { useDrag, useDrop } from 'react-dnd';
import { Menu } from '@material-ui/icons';

const DraggableTableRow = ({ isDraggable, index, moveRow, children, onClick, onMouseLeave }) => {
  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: 'TABLE_ROW',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const [, dropRef] = useDrop({
    accept: 'TABLE_ROW',
    drop: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveRow(draggedItem.index, index);
        draggedItem.index = index;
      }
    }
  });

  return (
    <TableRow
      ref={(node) => {
        previewRef(node);
        dropRef(node);
      }}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      sx={{
        cursor: 'pointer',
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : 'inherit'
      }}>
      {isDraggable && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'move' }}>
            <Icon
              color="primary"
              ref={(node) => {
                dragRef(node);
              }}>
              <Menu size={18} />
            </Icon>
          </Box>
        </TableCell>
      )}
      {children}
    </TableRow>
  );
};

DraggableTableRow.propTypes = {
  isDraggable: PropTypes.bool,
  item: PropTypes.object,
  index: PropTypes.number,
  moveRow: PropTypes.func,
  children: PropTypes.node,
  onClick: PropTypes.func,
  onMouseLeave: PropTypes.func
};

export const PromotionsTableBody = ({
  data,
  setData,
  isDraggable,
  openModal,
  setCurID,
  permissions
}) => {
  const history = useHistory();
  const [tooltip, setTooltip] = useState(false);

  const moveRow = (dragIndex, hoverIndex) => {
    const draggedRow = data[dragIndex];
    const updatedData = [...data];
    updatedData.splice(dragIndex, 1);
    updatedData.splice(hoverIndex, 0, draggedRow);
    setData(updatedData);
  };

  const tableRow = (component, key) =>
    tooltip ? (
      <Tooltip key={key} title={DISABLED_CONTROL_TOOLTIP} arrow data-testid="Tooltip">
        {component}
      </Tooltip>
    ) : (
      component
    );

  const navigateToPartnersDetailPage = (id) =>
    history.push(getRouteWithParam(ROUTES.PARTNERS_DETAIL_PROMOTION, ':id', id));

  return (
    <TableBody data-testid="TableBody">
      {data?.map((item, index) =>
        tableRow(
          <DraggableTableRow
            isDraggable={isDraggable}
            key={item.id}
            item={item}
            index={index}
            data-testid="TableRow"
            onClick={(e) => {
              if (!permissions.retrieve) setTooltip(true);
              else if (e.target.localName !== 'svg') {
                localStorage.setItem('partnersTab', 'PROMOTIONS');
                navigateToPartnersDetailPage(item.id);
              }
            }}
            onMouseLeave={() => setTooltip(false)}
            moveRow={moveRow}
            sx={{ cursor: permissions.retrieve ? 'pointer' : 'default' }}>
            <TableCell>
              <Box width="100px">{item.id}</Box>
            </TableCell>

            <TableCell>
              <Box width="180px">{item.cms_package_name}</Box>
            </TableCell>

            <TableCell>
              <Box width="250px" sx={{ display: 'flex' }}>
                <Avatar
                  alt={item?.partner_name}
                  src={`${ROUTE_FOR_ASSETS}/partners/${item?.partner_logo}`}
                  sx={{ width: 36, height: 36 }}
                />
                <Box color="primary.main" ml="15px" sx={{ lineHeight: '36px', fontWeight: 500 }}>
                  {item.partner_name}
                </Box>
              </Box>
            </TableCell>

            <TableCell>
              <Box width="180px">{formatDate(item.date_start)}</Box>
            </TableCell>

            <TableCell>
              <Box width="180px">{formatDate(item.date_end, true)}</Box>
            </TableCell>

            <TableCell>
              <Box color={`${getMailingColorStatus(item.status)}`}>{item.status}</Box>
            </TableCell>

            <TableCell onClick={(e) => e.stopPropagation()}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <More
                  id={item.id}
                  openModal={openModal}
                  setCurID={setCurID}
                  status={item.status}
                  permissions={permissions}
                />
              </Box>
            </TableCell>
          </DraggableTableRow>,
          item.id
        )
      )}
    </TableBody>
  );
};

PromotionsTableBody.propTypes = {
  data: PropTypes.array,
  setData: PropTypes.func,
  isDraggable: PropTypes.bool,
  openModal: PropTypes.func,
  setCurID: PropTypes.func,
  permissions: PropTypes.object
};
